// API service for backend policy endpoints
import { BaseApiService } from "./baseApiService";
import type { BaseResponse, PolicyDocument } from "../types/api";

export interface PolicyListResponse extends BaseResponse {
  data: PolicyDocument[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface LanguageOption {
  code: string;
  display: string;
}

export interface AvailableLanguagesResponse {
  data: LanguageOption[];
  success: boolean;
}

export interface PolicyApiParams {
  page?: number;
  limit?: number;
  document_type?: string;
  category?: string;
  subcategory?: string;
  audience_id?: number;
  folder_id?: number;
  language?: string;
  signal?: AbortSignal; // Support request cancellation
}

class PolicyApiService extends BaseApiService {
  // Get policy documents list
  async getPolicies(params: PolicyApiParams = {}): Promise<PolicyListResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.document_type)
      queryParams.append("document_type", params.document_type);
    if (params.category) queryParams.append("category", params.category);
    if (params.subcategory)
      queryParams.append("subcategory", params.subcategory);
    if (params.audience_id)
      queryParams.append("audience_id", params.audience_id.toString());
    if (params.folder_id)
      queryParams.append("folder_id", params.folder_id.toString());
    if (params.language) queryParams.append("language", params.language);

    const url = `/api/v1/policies?${queryParams.toString()}`;

    // Pass abort signal to support request cancellation
    const response = await BaseApiService.get({ url, signal: params.signal });

    if (response.success) {
      return response as unknown as PolicyListResponse;
    } else {
      throw new Error(response.error || "Failed to fetch policies");
    }
  }

  // Get available languages for policy documents
  async getAvailableLanguages(): Promise<AvailableLanguagesResponse> {
    const url = `/api/v1/languages`;

    const response = await BaseApiService.get({ url });

    if (response.success) {
      return response as AvailableLanguagesResponse;
    } else {
      throw new Error(response.error || "Failed to fetch available languages");
    }
  }
}

// Export singleton instance
export const policyApiService = new PolicyApiService();

// Export the class for testing or custom instances
export default PolicyApiService;
